<template>
	<div class="bg5 p2">
		<div class="xs-12 flex wrap xs-between">
			<div class="fw7 fs4 c6 lh125">{{ productName }}</div>
			<div>
				<j-button
					:class="productType === 'retail' ? 'secondary' : 'quaternary'"
					size="small"
					@click="switchProductType('retail')"
					:isDisabled="disablePreset"
					>{{ t("retail") }}</j-button
				>
				<j-button
					:class="productType === 'wholesale' ? 'secondary' : 'quaternary'"
					size="small"
					class="ml1"
					@click="switchProductType('wholesale')"
					:isDisabled="disablePreset"
					>{{ t("wholesale") }}</j-button
				>
			</div>
		</div>
		<!-- header -->
		<div class="xs-12 mt6 flex">
			<div class="xs-6 fs3 fw7">{{ t("key") }}</div>
			<div class="xs-3 fs3 fw7">{{ t("rate") }} %</div>
			<div class="xs-3 fs3 fw7">{{ t("value") }}</div>
		</div>

		<!-- list -->
		<div class="mt2 mb6">
			<div
				v-for="(value, name) in values"
				:key="name"
				class="flex xs-12 pb1 xs-middle"
			>
				<div class="xs-6 fs3" :class="{ mt6: name === 'deductible' }">
					{{ label[name] }}
				</div>

				<!-- rate -->
				<div class="xs-3 pr1" :class="{ mt6: name === 'deductible' }">
					<core-form-input
						componentName="j-base-input-number"
						:name="`${name}-rate`"
						type="float"
						size="small"
						v-model="value.rate"
						:v-validate="'required'"
						:validationErrors="errors"
						aria-autocomplete="none"
						@input="handleInput"
					/>
				</div>
				<!-- value -->
				<div class="xs-3" :class="{ mt6: name === 'deductible' }">
					<core-form-input
						componentName="j-base-input-number"
						:name="`${name}-value`"
						type="float"
						size="small"
						:isReadOnly="name === 'deductible' ? true : false"
						:status="checkForErrors(`${name}-value`)"
						v-model="value.value"
						aria-autocomplete="none"
						@input="handleInput"
					/>
				</div>
			</div>
		</div>

		<!-- Applied Taxes -->
		<div
			class="mb3 flex xs-around xs-middle"
			v-for="(item, i) in computedTaxes"
			:key="item.name"
		>
			<div class="xs-6">
				<span class="fs3 fw4">{{ item.name }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fs3 fw4">{{ item.value }}</span>
			</div>
		</div>

		<!-- Pricing -->
		<div
			class="mb3 flex xs-around xs-middle"
			v-for="(item, i) in computedFormattedPricing"
			:key="item.name"
		>
			<div class="xs-6">
				<span class="fs3 fw7">{{ t(item.name) }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fs3 fw7">{{ item.value }}</span>
			</div>
		</div>

		<!-- Save value -->
		<j-button class="mt6" size="small" @click="saveValues">
			{{ t("save_values") }}
		</j-button>

		<!-- Reset value -->
		<j-button
			class="mt6 ml1"
			type="secondary"
			size="small"
			@click="resetValues"
			>{{ t("reset_values") }}</j-button
		>
	</div>
</template>

<script>
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import debounce from "lodash.debounce"
import { parseLiquid } from "@/api/utils.js"

import JButton from "@/components/JButton"
import CoreFormInput from "@/modules/core/components/CoreFormInput"

const presetValues = {
	retail: {
		travelling: {
			rate: 0,
			value: 0
		},
		premises_stock_1: {
			rate: 0,
			value: 0
		},
		furniture_fixture_fitting: {
			rate: 0,
			value: 0
		}
	},
	wholesale: {
		travelling: {
			rate: 0,
			value: 0
		},
		premises_stock_1: {
			rate: 0,
			value: 0
		},
		furniture_fixture_fitting: {
			rate: 0,
			value: 0
		}
	}
}

export default {
	props: {
		productId: {
			validator(value) {
				return typeof value === "object"
			}
		},
		caseId: String,
		proposalId: String,
		offerId: String
	},

	components: {
		CoreFormInput,
		JButton
	},

	data() {
		return {
			productType: "retail", // retail || wholesale || custom
			productName: "",
			pricing: null,
			values: null,
			disablePreset: null,
			clauses: null
		}
	},

	computed: {
		computedValues() {
			return this.values
		},
		computedFormattedPricing() {
			return [
				{ name: "gross_amount", value: this.pricing?.formattedOriginalPrice },
				{
					name: "total_amount",
					value: this.pricing?.formattedPrice
				}
			]
		},
		computedTaxes() {
			return this.pricing?.appliedTaxes?.map(item => {
				return {
					name: item.name ? item.name : item.code,
					value: this.getValue(item).flat
				}
			})
		},
		plan() {
			return this.productId?.plan
		},
		label() {
			return {
				travelling: "Travelling",
				premises_stock_1: "Premises Stock",
				furniture_fixture_fitting: "F.F.F.",
				deductible: "Deductible"
			}
		}
	},

	methods: {
		t(key) {
			const locale = {
				"en-US": {
					key: "Key",
					rate: "Rate",
					save_values: "Save Values",
					reset_values: "Reset Values",
					gross_amount: "Gross Amount",
					total_amount: "Total Amount",
					retail: "Retail",
					wholesale: "Wholesale",
					value: "Value",
					values_saved: "Values Saved"
				},
				"zh-HK": {}
			}
			return locale[this.$store.state.settings.locale][key]
		},

		applyPresetValues() {
			// deep clone so that original presetValues is not mutated
			this.values = JSON.parse(JSON.stringify(presetValues[this.productType]))
		},

		resetValues() {
			this.productType = "retail"
			this.disablePreset = false
			this.applyPresetValues()
		},

		switchProductType(type) {
			this.productType = type
			this.applyPresetValues()
			this.fetchPrice()
		},

		async fetchPrice() {
			const isValid = await this.$validator.validate()
			if (!isValid) return

			const query = gql`
				query getPrice($where: productWhereInput, $values: [keyValueInput]) {
					products: products_2(where: $where, values: $values) {
						list {
							name
							productId {
								plan
							}
							pricing(values: $values) {
								appliedTaxes {
									name
									code
									ratio
									formattedFlat
								}
								originalPrice
								formattedOriginalPrice
								amount
								formattedPrice
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					where: {
						productId: {
							plan: this.plan
						}
					},
					values: this.massageValuesForAPI()
				}
			})

			this.productName = res?.data?.products?.list?.[0]?.name
			this.pricing = res?.data?.products?.list?.[0]?.pricing
		},

		handleInput: debounce(function() {
			this.fetchPrice()
			this.productType = "custom"
		}, 600),

		async fetchValuesAndClauses() {
			const query = gql`
				fragment ObjectValue on scalarValue {
					objectValue {
						key
						value {
							objectValue {
								key
								value {
									numberValue
								}
							}
						}
					}
				}

				query fetchValuesAndClauses(
					$caseWhere1: caseWhere
					$proposalWhere1: proposalWhere
					$offerWhere1: offerWhere
				) {
					cases(where: $caseWhere1) {
						list {
							id
							proposals(where: $proposalWhere1) {
								basket(where: $offerWhere1) {
									id
									values {
										key
										value {
											arrayValue {
												...ObjectValue
											}
										}
									}
									clauses {
										renderParameters {
											contentJsonString
										}
										template {
											... on clauseHtml {
												html
											}
										}
									}
								}
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					caseWhere1: { id: this.caseId },
					proposalWhere1: { id: this.proposalId },
					offerWhere1: { id: this.offerId }
				}
			})

			this.clauses = res?.data?.cases?.list?.[0]?.proposals?.[0]?.basket?.[0]?.clauses?.map(
				item => ({
					variables: item?.renderParameters?.contentJsonString,
					htmlString: item?.template?.html
				})
			)

			// if offer has no values, we apply preset values
			if (!res?.data?.cases?.list?.[0]?.proposals?.[0]?.basket?.[0]?.values) {
				this.disablePreset = false
				this.applyPresetValues()
				return
			}

			this.disablePreset = true
			this.productType = "custom"

			this.values = this.massageValuesFromAPI(
				res?.data?.cases?.list?.[0]?.proposals?.[0]?.basket?.[0]?.values
			)
		},

		async saveValues() {
			// Validate
			const isValid = await this.$validator.validate()
			if (!isValid) {
				this.$store.dispatch("showError", [
					"There are some missing or incorrect fields in the form"
				])
				return
			}

			const query = gql`
				mutation updateOfferOfProposal(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$updateOfferInput: updateOfferInput!
				) {
					updateOfferOfProposal(
						caseId: $caseId
						offerId: $offerId
						proposalId: $proposalId
						updateOfferInput: $updateOfferInput
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				updateOfferInput: {
					values: this.massageValuesForAPI()
				}
			}

			const res = await api({
				query,
				variables
			})

			if (!res?.data?.updateOfferOfProposal?.errors)
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.t("values_saved")
					}
				})

			this.disablePreset = true
			this.$emit("premium:updated")
		},

		massageValuesFromAPI(values) {
			return values
				?.find(item => item.key === "insureds")
				?.value?.arrayValue?.[0]?.objectValue?.reduce((obj, item) => {
					let key = item?.key
					let value = item?.value?.objectValue.reduce((innerObj, innerItem) => {
						let innerKey = innerItem?.key
						let innerValue =
							innerKey === "rate"
								? parseFloat((innerItem?.value?.numberValue * 100).toFixed(6))
								: innerItem?.value?.numberValue
						innerObj[innerKey] = innerValue
						return innerObj
					}, {})
					obj[key] = value
					return obj
				}, {})
		},

		massageValuesForAPI() {
			return [
				{
					key: "insureds",
					value: {
						arrayValue: [
							{
								objectValue: Object.keys(this.computedValues).map(key => {
									return {
										key,
										value: {
											objectValue: [
												{
													key: "rate",
													value: {
														numberValue: this.computedValues[key].rate / 100
													}
												},
												{
													key: "value",
													value: {
														numberValue: this.computedValues[key].value
													}
												}
											]
										}
									}
								})
							}
						]
					}
				}
			]
		},

		applyDeductibleClause() {
			// filter clauses with liquid {{ data.deductible }}
			const deductibleClauses = this?.clauses
				?.map(item => {
					return {
						...item,
						ast: parseLiquid(item?.htmlString)
					}
				})
				?.filter(item =>
					item?.ast?.find(obj => obj?.value?.initial === "data.deductible")
				)

			if (!deductibleClauses) return

			// we only care about the first deductible added
			// default value from liquid {{ data.detuctible | default: defaultValueHere }}
			const defaultValue = deductibleClauses?.[0]?.ast
				?.find(item => item?.value?.initial === "data.deductible")
				?.value?.filters?.find(item => item.name === "default")?.args?.[0]

			// user input in clauses.renderParameters.contentJsonString
			const userInputValue = deductibleClauses?.[0]?.variables
				? JSON?.parse(deductibleClauses?.[0]?.variables)?.deductible
				: null

			if (deductibleClauses?.length === 0) {
				delete this.values?.deductible
			} else
				this.values = {
					...this.values,
					deductible: {
						rate: 2.5,
						value: userInputValue
					}
				}
		},

		checkForErrors(fieldName) {
			if (
				this.errors?.items?.filter(item => item.field === fieldName)?.length > 0
			)
				return "error"
			else return null
		},

		async onClausesUpdate() {
			await this.fetchValuesAndClauses()
			await this.applyDeductibleClause()
			await this.fetchPrice()
			this.saveValues()
		},

		getValue(item) {
			if (!item) return ""
			const ratio = item.ratio ? item.ratio * 100 : null
			const flat = item.formattedFlat ? item.formattedFlat : "0"
			return {
				flat,
				summary: ratio
					? `(${ratio ? ratio.toFixed(2) : "0"}% of ${
							item.formattedOriginalPrice
					  })`
					: ""
			}
		}
	},

	async created() {
		await this.fetchValuesAndClauses()
		await this.applyDeductibleClause()
		await this.fetchPrice()
		this.$store.dispatch("caseManagement/subscribe", {
			eventName: "clauses:updated",
			callback: this.onClausesUpdate
		})
	},

	watch: {
		// When productId updates, refetch data
		// This is need because we use same component for different productId
		// And thus we have to refetch as AegPricing doesn't remount the component
		productId: {
			handler() {
				this.fetchValuesAndClauses()
				this.fetchPrice()
			}
		},
		// when offerId changes,
		// reset productType to default
		// and refetch values
		offerId: async function() {
			this.productType = "retail"
			await this.fetchValuesAndClauses()
		}
	}
}
</script>
